import React, { useState, useEffect } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { SpecialInstruction } from "../../../../../../../../models/special.instruction";
import { Status } from "../../../../../../../../util/enums";
import Input from "../../../../../../../../components/Form/Input";
import Modal from "../../../../../../../../components/Generic/Modal";
import useStyles from "./styles";

interface Props {
  specialInstruction?: SpecialInstruction;
  open: boolean;
  hasError?: boolean;
  isLoading?: boolean;
  closeModal: () => void;
  updateOrCreateSpecialInstruction: (specialInstruction: SpecialInstruction) => void;
}

const SpecialInstructionsModal = ({
  specialInstruction,
  open,
  hasError,
  isLoading = false,
  closeModal,
  updateOrCreateSpecialInstruction,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [description, setDescription] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [status, setStatus] = useState<string>(Status.INACTIVE);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const schema = yup.object().shape({
    code: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (type) =>
        type ? type !== "" : false
      ),
    description: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (type) =>
        type ? type !== "" : false
      ),
  });

  const clearFields = () => {
    setDescription("");
    setCode("");
  };

  const isValid = (specialInstruction: SpecialInstruction) => {
    try {
      schema.validateSync(
        {
          code: specialInstruction.code,
          description: specialInstruction.description,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    const object = {
      description,
      code,
      status,
    } as SpecialInstruction;

    if (isValid(object)) updateOrCreateSpecialInstruction(object);
  };

  useEffect(() => {
    if (specialInstruction) {
      setDescription(specialInstruction?.description ? specialInstruction?.description : "");
      setCode(specialInstruction?.code ? specialInstruction?.code : "");
      setStatus(specialInstruction?.status ? specialInstruction?.status : Status.INACTIVE);
    }
  }, [specialInstruction]);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      clearFields();
      closeModal();
    }
  }, [hasError]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      const object = {
        description,
        code,
        status,
      } as SpecialInstruction;

      isValid(object);
    }
  }, [description, code, status, hasFormBeenSubmitted]);

  return (
    <Modal
      data-cy="specialInstructionModal"
      open={open}
      handleClose={closeModal}
      title={
        specialInstruction ? translate("editItemInstruction") : translate("createItemInstruction")
      }
      showActionButtons
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: () => {
          clearFields();
          closeModal();
        },
        dataCySave: "submitButton",
        textSave: specialInstruction ? translate("update") : translate("create"),
        loading: isLoading,
        onSave: handleSubmit,
      }}
      labelStatus={translate("status")}
      showStatusField={true}
      statusFieldProps={{
        label: translate("itemInstructionStatus"),
        value: status,
        dataCy: "selectStatus",
        placeholder: translate("selectAnOption"),
        handleChange: (value) => setStatus(value),
      }}
    >
      <section className={classes.modalSection}>
        <Grid container direction="row" alignItems="flex-start" spacing={2}>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isLoading}
              name="code"
              label={translate("code")}
              placeholder={translate("code")}
              error={!!getErrorByField("code")}
              errorMessage={getErrorByField("code")}
              value={code}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCode(event.target.value)}
            />
          </Grid>
          <Grid item md={6} lg={6}>
            <Input
              disabled={isLoading}
              name="description"
              label={translate("name")}
              placeholder={translate("name")}
              error={!!getErrorByField("description")}
              errorMessage={getErrorByField("description")}
              value={description}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setDescription(event.target.value)
              }
            />
          </Grid>
        </Grid>
      </section>
    </Modal>
  );
};

export default SpecialInstructionsModal;
